// client/src/components/ticketing/PricePhaseTimeline.jsx
//
// Price phases for one ticket tier (Growth Phase 3): early-bird, advance and
// so on, shown as a timeline that ends at the tier's own (regular) price.
// The first phase by order whose window is open and that still has seats
// left is the one buyers pay; the server works that out (ticketingService
// .computeQuote), this panel only edits the phases and mirrors the rule to
// mark "on sale now". Rendered inside TicketConfig's tier list, so it uses
// the AuctionConfig.css / TicketConfig.css dark theme.

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/currencyUtils";

const EMPTY = { label: "", price: "", starts_at: "", ends_at: "", quantity_limit: "", sort_order: "" };

// 'YYYY-MM-DD HH:MM:SS' (mysql2 dateStrings) → datetime-local value.
const toInput = (v) => (v ? String(v).replace(" ", "T").slice(0, 16) : "");

function formatWhen(v) {
  if (!v) return "";
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Same rule as the server: first by sort_order, window open, seats left. */
export function currentPhaseId(phases, now = new Date()) {
  const sorted = [...phases].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id);
  const open = sorted.find((p) => {
    const start = p.starts_at ? new Date(String(p.starts_at).replace(" ", "T")) : null;
    const end = p.ends_at ? new Date(String(p.ends_at).replace(" ", "T")) : null;
    const left = p.quantity_limit == null ? Infinity : Number(p.quantity_limit) - Number(p.quantity_sold || 0);
    return (!start || start <= now) && (!end || now < end) && left > 0;
  });
  return open ? open.id : null;
}

export default function PricePhaseTimeline({ tenantId, eventId, itemId, tier }) {
  const { t } = useTranslation();
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}/tiers/${tier.id}/phases`;
  const [phases, setPhases] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { kind, text }
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(base);
      if (data.success) setPhases(data.phases || []);
    } catch (err) {
      if (err?.response?.status === 409) setUnavailable(true);
      else setNotice({ kind: "error", text: t("ticketing.phases.loadError", "Could not load price phases.") });
    }
  }, [base, t]);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      label: p.label || "",
      price: String(p.price ?? ""),
      starts_at: toInput(p.starts_at),
      ends_at: toInput(p.ends_at),
      quantity_limit: p.quantity_limit != null ? String(p.quantity_limit) : "",
      sort_order: String(p.sort_order ?? ""),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    const payload = {
      label: form.label.trim(),
      price: Number(form.price),
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      quantity_limit: form.quantity_limit === "" ? null : Number(form.quantity_limit),
      sort_order: form.sort_order === "" ? phases.length : Number(form.sort_order),
    };
    try {
      if (editingId) await axios.put(`${base}/${editingId}`, payload);
      else await axios.post(base, payload);
      setNotice({ kind: "success", text: t("ticketing.phases.saved", "Price phase saved.") });
      reset();
      load();
    } catch (err) {
      setNotice({ kind: "error", text: err?.response?.data?.message || t("ticketing.phases.saveError", "Could not save the price phase.") });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("ticketing.phases.confirmRemove", "Remove the {{label}} price?", { label: p.label }))) return;
    try {
      await axios.delete(`${base}/${p.id}`);
      if (editingId === p.id) reset();
      load();
    } catch (err) {
      setNotice({ kind: "error", text: err?.response?.data?.message || t("ticketing.phases.saveError", "Could not save the price phase.") });
    }
  };

  if (unavailable) {
    return (
      <div className="ticket-subpanel">
        <div className="ticket-notice">
          {t("ticketing.phases.notReady", "Price phases need a database update before they can be used.")}
        </div>
      </div>
    );
  }

  const nowId = currentPhaseId(phases);
  const sorted = [...phases].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id);
  const currency = tier.currency;

  return (
    <div className="ticket-subpanel" data-testid={`phases-${tier.id}`}>
      {notice && <div className={`message message-${notice.kind}`}>{notice.text}</div>}

      <ol className="ticket-timeline" aria-label={t("ticketing.phases.timeline", "Price timeline")}>
        {sorted.map((p) => {
          const span = [
            p.starts_at ? t("ticketing.phases.from", "from {{when}}", { when: formatWhen(p.starts_at) }) : null,
            p.ends_at ? t("ticketing.phases.until", "until {{when}}", { when: formatWhen(p.ends_at) }) : null,
          ].filter(Boolean).join(" ");
          return (
            <li key={p.id} className={`ticket-timeline-step${p.id === nowId ? " current" : ""}`}>
              <div className="ticket-timeline-row">
                <div>
                  <div className="ticket-list-item-title">
                    {p.label} · {formatCurrency(p.price, currency)}
                    {p.id === nowId && <span className="ticket-badge accent">{t("ticketing.phases.onSaleNow", "On sale now")}</span>}
                  </div>
                  <div className="ticket-list-item-meta">
                    {span || t("ticketing.phases.noWindow", "No time limit")}
                    {p.quantity_limit != null && (
                      <> · {t("ticketing.phases.soldOfLimit", "{{sold}} of {{limit}} sold", { sold: p.quantity_sold || 0, limit: p.quantity_limit })}</>
                    )}
                  </div>
                </div>
                <div className="ticket-list-item-actions">
                  <button type="button" className="ticket-item-btn" onClick={() => startEdit(p)}>
                    {t("ticketing.phases.edit", "Edit")}
                  </button>
                  <button type="button" className="ticket-item-btn danger" onClick={() => remove(p)}>
                    {t("ticketing.phases.remove", "Remove")}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
        <li className={`ticket-timeline-step${nowId == null ? " current" : ""}`}>
          <div className="ticket-list-item-title">
            {t("ticketing.phases.regular", "Regular price")} · {formatCurrency(tier.price, currency)}
            {nowId == null && <span className="ticket-badge accent">{t("ticketing.phases.onSaleNow", "On sale now")}</span>}
          </div>
          <div className="ticket-list-item-meta">
            {t("ticketing.phases.regularHint", "Applies when no phase above is open or has seats left.")}
          </div>
        </li>
      </ol>

      <form onSubmit={submit} className="auction-config-form" style={{ padding: 12, borderRadius: 8 }}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor={`phase_label_${tier.id}`}>{t("ticketing.phases.label", "Phase name")} <span className="required">*</span></label>
            <input
              id={`phase_label_${tier.id}`}
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder={t("ticketing.phases.labelPlaceholder", "Early bird")}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor={`phase_price_${tier.id}`}>{t("ticketing.phases.price", "Phase price")} <span className="required">*</span></label>
            <input
              id={`phase_price_${tier.id}`}
              type="number" min="0" step="0.01"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor={`phase_start_${tier.id}`}>{t("ticketing.phases.startsAt", "Starts")}</label>
            <input
              id={`phase_start_${tier.id}`}
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`phase_end_${tier.id}`}>{t("ticketing.phases.endsAt", "Ends")}</label>
            <input
              id={`phase_end_${tier.id}`}
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor={`phase_limit_${tier.id}`}>{t("ticketing.phases.quantityLimit", "Tickets at this price")}</label>
            <input
              id={`phase_limit_${tier.id}`}
              type="number" min="1"
              value={form.quantity_limit}
              onChange={(e) => setForm((f) => ({ ...f, quantity_limit: e.target.value }))}
              placeholder={t("ticketing.phases.noLimit", "Blank = no limit")}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`phase_order_${tier.id}`}>{t("ticketing.phases.order", "Order")}</label>
            <input
              id={`phase_order_${tier.id}`}
              type="number" min="0"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              placeholder={String(phases.length)}
            />
          </div>
        </div>
        <div className="form-actions" style={{ marginTop: 8, paddingTop: 8 }}>
          <button type="submit" disabled={saving} className="save-button">
            {saving
              ? t("ticketing.phases.saving", "Saving…")
              : editingId ? t("ticketing.phases.update", "Update phase") : t("ticketing.phases.add", "Add phase")}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="delete-button" style={{ background: "#5a5a55" }}>
              {t("ticketing.phases.cancel", "Cancel")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
